/**
 * The compact dossier panel of the domain page ("Hồ sơ & định danh", owner 2026-10-01: one panel
 * like HostBill's — upload, the uploaded files, the dossier on VNNIC). What each file's status is
 * and why, what still blocks the VNNIC submission, and the short checklist — as codes the page
 * translates, never the server's own (English) text.
 *
 * Pure functions (no React): `npm test` covers them.
 */

import type { DossierVnnicFiles, HstmBoundFile } from '../api/types';

export type FileState = 'APPROVED' | 'PENDING' | 'REJECTED';

export type FileReason =
  | 'EKYC_AUTO'
  | 'SIGNATURE_AUTO'
  | 'STAFF_APPROVED'
  | 'AUTO_CHECK'
  | 'ORG_DECLARATION'
  | 'AWAITING_REGISTRAR'
  | 'AWAITING_STAFF'
  | 'GENERATED'
  | 'AWAITING_IDENTITY'
  | 'AWAITING_CHECK'
  | 'STORED'
  | 'FORMAT'
  | 'SIZE'
  | 'DIMENSIONS'
  | 'CHECK_FAILED'
  | 'OTHER';

/** Why a file was refused by VNNIC's file rule (`vnnic_files.rejected[].problem`), as a code. */
export function problemCode(problem: string | null | undefined): FileReason {
  const p = problem ?? '';
  if (/px outside/i.test(p)) return 'DIMENSIONS';
  if (/bytes outside/i.test(p)) return 'SIZE';
  if (/ is not /i.test(p)) return 'FORMAT';
  return 'OTHER';
}

export interface FileFacts {
  /** VNNIC HSTM file type, when the file is one. */
  fileType: number | null;
  bound?: Pick<HstmBoundFile, 'signed_by_subject' | 'signed_by_registrar'> | null;
  /** The document's own check (`PASSED`, `VERIFIED`, `PENDING`, `FAILED`, `REJECTED`). */
  check?: string | null;
  /** VNNIC's file rule refused it (format, size …). */
  rejectedProblem?: string | null;
  dossierComplete: boolean;
  /** HSTM certification method of the dossier (1 eKYC, 2 / 4 digital signature, 5 manual). */
  certifyMethod: number | null;
  /** The plan requires this file type signed by the registrar. */
  needsRegistrarSignature: boolean;
  /** The actual prerequisite; a generated template is not a submission awaiting staff. */
  pendingReason?: 'GENERATED' | 'AWAITING_IDENTITY' | 'AWAITING_CHECK' | 'STORED';
}

/** A file's status ("Đã duyệt / Chờ duyệt / Từ chối") and the reason shown under it. */
export function fileStatus(f: FileFacts): { state: FileState; reason: FileReason } {
  if (f.rejectedProblem !== undefined && f.rejectedProblem !== null) return { state: 'REJECTED', reason: problemCode(f.rejectedProblem) };
  if (f.check === 'FAILED' || f.check === 'REJECTED') return { state: 'REJECTED', reason: 'CHECK_FAILED' };
  const registrarPending = f.needsRegistrarSignature && !f.bound?.signed_by_registrar;
  const approved = !registrarPending && (f.dossierComplete || f.bound?.signed_by_registrar || f.bound?.signed_by_subject || f.check === 'PASSED' || f.check === 'VERIFIED');
  if (approved) {
    if (f.bound?.signed_by_subject && !f.bound.signed_by_registrar) return { state: 'APPROVED', reason: 'SIGNATURE_AUTO' };
    switch (f.certifyMethod) {
      case 1:
      case 3:
        return { state: 'APPROVED', reason: 'EKYC_AUTO' };
      case 2:
      case 4:
        return { state: 'APPROVED', reason: 'SIGNATURE_AUTO' };
      case 5:
        return { state: 'APPROVED', reason: 'STAFF_APPROVED' };
      default:
        return { state: 'APPROVED', reason: 'AUTO_CHECK' };
    }
  }
  return { state: 'PENDING', reason: f.pendingReason ?? (registrarPending ? 'AWAITING_REGISTRAR' : 'AWAITING_STAFF') };
}

export type ErrorCode = 'NO_DOC_TYPE' | 'NO_VERIFICATION' | 'METHOD_NOT_ACCEPTED' | 'CASE_UNSUPPORTED' | 'NO_CASE' | 'EKYC_REQUEST_ID' | 'EKYC_SCORE' | 'SIGNER_UNKNOWN' | 'OTHER';

/** A reason the HSTM plan gives (`vnnic_files.errors`, English, for logs) as a code. */
export function errorCode(message: string): ErrorCode {
  const m = message.trim();
  if (/has no HSTM dossier type/i.test(m)) return 'NO_DOC_TYPE';
  if (/has no subject verification/i.test(m)) return 'NO_VERIFICATION';
  if (/has no HSTM certification method/i.test(m)) return 'METHOD_NOT_ACCEPTED';
  if (/is not supported with certification method/i.test(m)) return 'CASE_UNSUPPORTED';
  if (/^no Phụ lục 2 case/i.test(m)) return 'NO_CASE';
  if (/eKYC request id/i.test(m)) return 'EKYC_REQUEST_ID';
  if (/face-match/i.test(m)) return 'EKYC_SCORE';
  if (/signer and signing date/i.test(m)) return 'SIGNER_UNKNOWN';
  return 'OTHER';
}

export type Blocker =
  | { kind: 'missing'; alternatives: number[][]; signedStatus: number }
  | { kind: 'error'; code: ErrorCode }
  | { kind: 'rejected'; fileType: number; problem: FileReason };

/** What keeps the dossier from being sent to VNNIC, in order: missing files, then the plan's reasons. */
export function vnnicBlockers(files: Pick<DossierVnnicFiles, 'missing' | 'errors' | 'rejected'> | null | undefined): Blocker[] {
  if (!files) return [];
  const out: Blocker[] = files.missing.map((m) => ({ kind: 'missing', alternatives: m.file_types, signedStatus: m.signed_status }));
  const codes = new Set<ErrorCode>();
  for (const e of files.errors) codes.add(errorCode(e));
  for (const code of codes) out.push({ kind: 'error', code });
  for (const r of files.rejected) out.push({ kind: 'rejected', fileType: r.file_type, problem: problemCode(r.problem) });
  return out;
}

/** The dossier on VNNIC, from the HSTM view when the server gives one, else from the sync state. */
export type VnnicState = 'APPROVED' | 'PENDING' | 'REJECTED' | 'NOT_SENT';

export function vnnicState(hstmStatus: string | null | undefined, syncStatus: string | null | undefined): VnnicState {
  const h = (hstmStatus ?? '').toUpperCase();
  if (h === 'APPROVED' || h === 'PENDING' || h === 'REJECTED' || h === 'NOT_SENT') return h;
  switch (syncStatus) {
    case 'ACKNOWLEDGED':
      return 'APPROVED';
    case 'SUBMITTING':
    case 'SUBMITTED':
    case 'RESUBMIT_PENDING':
      return 'PENDING';
    case 'REJECTED':
    case 'CORRECTION_REQUIRED':
      return 'REJECTED';
    default:
      return 'NOT_SENT';
  }
}

export type CheckState = 'ok' | 'waiting' | 'missing' | 'unknown';

export interface ChecklistFacts {
  /** The holder's registration form exists / is accepted (null while loading). */
  declared: boolean | null;
  accepted: boolean | null;
  verified: boolean;
  rightSubject: boolean;
  /** `.vn` only (null otherwise). */
  files: Pick<DossierVnnicFiles, 'missing'> | null;
  registrarSigned: boolean;
  /** Null outside `.vn`, or while loading. */
  vnnic: VnnicState | null;
  vn: boolean;
}

export type ChecklistId = 'declared' | 'accepted' | 'verified' | 'subject' | 'files' | 'registrar' | 'vnnic';

/** The short checklist ("Kiểm tra nhanh"): one line per fact, in the order a dossier gets done. */
export function dossierChecklist(f: ChecklistFacts): { id: ChecklistId; state: CheckState; vnnic?: VnnicState }[] {
  const b = (v: boolean | null, pending = false): CheckState => (v === null ? 'unknown' : v ? 'ok' : pending ? 'waiting' : 'missing');
  const items: { id: ChecklistId; state: CheckState; vnnic?: VnnicState }[] = [
    { id: 'declared', state: b(f.declared) },
    { id: 'accepted', state: b(f.accepted, f.declared === true) },
    { id: 'verified', state: b(f.verified) },
    { id: 'subject', state: b(f.rightSubject) },
  ];
  if (f.vn) {
    // Files other than the registrar's signature (its own line).
    const files = f.files ? f.files.missing.filter((m) => m.signed_status !== 2).length === 0 : null;
    items.push({ id: 'files', state: b(files) });
    items.push({ id: 'registrar', state: f.registrarSigned ? 'ok' : f.declared ? 'waiting' : 'missing' });
    const v = f.vnnic;
    items.push({ id: 'vnnic', state: v === null ? 'unknown' : v === 'APPROVED' ? 'ok' : v === 'PENDING' ? 'waiting' : 'missing', vnnic: v ?? undefined });
  }
  return items;
}
